// Canonical ConnectWise + in-portal entity-link builders.
//
// Single source of truth for (a) ConnectWise deep-link URLs and (b) the
// portal-first routing decision (in-app route when a portal page exists, CW
// deep link otherwise). Previously the CW host/company were hard-coded here AND
// re-hard-coded in apps/web/src/lib/connectwise-links.tsx; that duplicate is
// gone — everything now flows through these builders. See issue #641 (audit
// item LNK-11).

// CW host + company are configurable so a non-prod tenant can be pointed
// elsewhere without editing source. Resolution order mirrors APP_VERSION
// (constants.ts): build-time define → runtime env → hard-coded default.
declare const __CW_LINK_BASE__: string | undefined;
declare const __CW_LINK_COMPANY__: string | undefined;
// Minimal ambient `process` so this browser-shared package typechecks without
// pulling in @types/node (TS 6 no longer resolves the Node global implicitly).
declare const process: { env?: Record<string, string | undefined> } | undefined;

function resolveCwBase(): string {
  if (typeof __CW_LINK_BASE__ !== 'undefined' && __CW_LINK_BASE__) return __CW_LINK_BASE__;
  if (typeof process !== 'undefined' && process.env?.CW_LINK_BASE) return process.env.CW_LINK_BASE;
  return 'https://na.myconnectwise.net/v4_6_release/services/system_io';
}

function resolveCwCompany(): string {
  if (typeof __CW_LINK_COMPANY__ !== 'undefined' && __CW_LINK_COMPANY__) return __CW_LINK_COMPANY__;
  if (typeof process !== 'undefined' && process.env?.CW_LINK_COMPANY) return process.env.CW_LINK_COMPANY;
  return 'harristech';
}

const CW_BASE = resolveCwBase();
const CW_COMPANY = resolveCwCompany();

function cwRecordUrl(recordType: string, recId: number): string {
  return `${CW_BASE}/router/openrecord.rails?companyName=${CW_COMPANY}&recordType=${recordType}&recid=${recId}`;
}

export function cwProjectUrl(recId: number): string {
  return cwRecordUrl('ProjectHeaderFV', recId);
}

export function cwServiceTicketUrl(ticketId: number): string {
  return cwRecordUrl('ServiceFV', ticketId);
}

export function cwCompanyUrl(companyRecId: number): string {
  return cwRecordUrl('CompanyFV', companyRecId);
}

export function cwPurchaseOrderUrl(poRecId: number): string {
  return cwRecordUrl('PurchaseOrderFV', poRecId);
}

export function cwInvoiceUrl(invoiceRecId: number): string {
  return cwRecordUrl('InvoiceFV', invoiceRecId);
}

/**
 * The set of entity types the portal knows how to link. Some have an in-app
 * page (project/ticket/company/project-finance) and some are CW-only
 * (purchase-order/invoice). Future CW-only types (schedule, rma, time-entry)
 * slot in here as their builders land.
 */
export type CwEntityType =
  | 'project'
  | 'ticket'
  | 'company'
  | 'project-finance'
  | 'purchase-order'
  | 'invoice';

/**
 * In-app route path for an entity when a portal page exists, else null.
 * The canonical portal-vs-CW decision: callers (and the web EntityLink
 * component) route through this instead of each deciding independently.
 * Paths mirror apps/web/src/app/registry.ts.
 *
 * PROJECT SURFACE CONTEXT RULE (#649): a project can be viewed on three pages —
 * detail (`/projects/:id`), review (`/projects/:id/review`), and finance
 * (`/finance/projects/:id`). To keep "click the project" predictable, choose the
 * type by the SURFACE you're linking from:
 *   - `project`         → detail: general/operational surfaces (dashboards,
 *                          Projects list, command palette, accounts, tickets).
 *   - `project-finance` → finance: surfaces in the Finance nav group
 *                          (Finance Overview, Margin, Open AR, Change Orders,
 *                          Forecasting, Parts Recon, Purchasing, Purchase Orders).
 * All three are one click apart via ProjectViewToggle, so the initial landing
 * only needs to match the user's current context, not be the "one true page".
 */
export function portalPathForEntity(type: CwEntityType, id: number): string | null {
  if (!Number.isFinite(id)) return null;
  switch (type) {
    case 'project':
      return `/projects/${id}`;
    case 'ticket':
      return `/tickets/${id}`;
    case 'company':
      return `/accounts/${id}`;
    case 'project-finance':
      // The standalone /finance/projects/:id page is retired; the project
      // review page's billing block is the maintained financial surface.
      return `/projects/${id}/review`;
    case 'purchase-order':
    case 'invoice':
      return null;
    default:
      return null;
  }
}

/**
 * ConnectWise deep-link URL for a typed entity, or null when no builder exists
 * or the id is non-numeric. project-finance maps to the underlying CW project
 * record.
 */
export function cwUrlForEntity(type: CwEntityType, id: number): string | null {
  if (!Number.isFinite(id)) return null;
  switch (type) {
    case 'project':
    case 'project-finance':
      return cwProjectUrl(id);
    case 'ticket':
      return cwServiceTicketUrl(id);
    case 'company':
      return cwCompanyUrl(id);
    case 'purchase-order':
      return cwPurchaseOrderUrl(id);
    case 'invoice':
      return cwInvoiceUrl(id);
    default:
      return null;
  }
}

// Normalizes a free-form AttentionItem.refType (or similar) to a CwEntityType,
// accepting the 'po' alias. Unknown/deferred types (e.g. 'schedule', 'rma')
// return null so no bogus link is built.
function normalizeRefType(refType: string): CwEntityType | null {
  switch (refType) {
    case 'project':
      return 'project';
    case 'ticket':
      return 'ticket';
    case 'company':
      return 'company';
    case 'invoice':
      return 'invoice';
    case 'purchase-order':
    case 'po':
      return 'purchase-order';
    default:
      return null;
  }
}

/**
 * Maps an AttentionItem refType/refId (loosely typed, string ids) to a
 * ConnectWise record URL, or null when no verified builder exists for that type
 * or the id is missing/non-numeric. Previously only handled project/ticket and
 * had no app callers wired to it (AttentionBar reimplemented the mapping
 * inline); now complete and used directly.
 */
export function cwUrlForRef(
  refType: string | undefined,
  refId: string | undefined,
): string | null {
  if (!refType || !refId) return null;
  const recId = Number(refId);
  if (!Number.isFinite(recId)) return null;
  const type = normalizeRefType(refType);
  return type ? cwUrlForEntity(type, recId) : null;
}
